import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import SpeakerButton from '@/components/SpeakerButton';
import PartnerAdRotator from '@/components/PartnerAdRotator';
import UpgradeNudge from '@/components/UpgradeNudge';
import { Plus, BookMarked, Gift, Users2, Sliders, Trophy, UserCog, Save, Flame, History, Play as PlayIcon } from 'lucide-react';

function DifficultyPicker({ child, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [value, setValue] = useState(child?.preferred_difficulty || (child?.age <= 7 ? 'easy' : child?.age <= 12 ? 'medium' : 'hard'));
    const set = async (v) => {
        if (v === value || busy) return;
        setBusy(true);
        try {
            await api.patch(`/me/children/${child.id}/difficulty`, { preferred_difficulty: v });
            setValue(v);
            onChanged?.(v);
        } finally { setBusy(false); }
    };
    return (
        <div className="bg-white rounded-2xl border border-lq-ink-100 p-5" data-testid="dashboard-difficulty-card">
            <div className="text-xs text-lq-ink-500 uppercase tracking-wider font-semibold inline-flex items-center gap-1"><Sliders size={12}/> Difficulty (parent only)</div>
            <div className="mt-1 font-display text-xl text-lq-ink-900">{child.display_name}</div>
            <p className="mt-1 text-sm text-lq-ink-500">Easy 4–7 · Medium 8–12 · Hard 13+. Affects grid size and balloon count.</p>
            <div className="mt-3 flex gap-2">
                {['easy', 'medium', 'hard'].map((d) => (
                    <button key={d} onClick={() => set(d)} disabled={busy}
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 transition-colors ${
                            value === d ? 'bg-lq-peach-500 text-white border-lq-peach-500' : 'bg-white border-lq-ink-100 text-lq-ink-900 hover:border-lq-peach-300'
                        }`}
                        data-testid={`dashboard-difficulty-${d}`}
                    >{d.charAt(0).toUpperCase()+d.slice(1)}</button>
                ))}
            </div>
        </div>
    );
}

export default function Dashboard() {
    const { user, children, activeChildId, pickActiveChild, refreshChildren, refreshUser } = useAuth();
    const [vocab, setVocab] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [fam, setFam] = useState(null);
    const [recent, setRecent] = useState({ activity: [], streak_days: 0, days_played_total: 0 });
    const [liveSub, setLiveSub] = useState(null);
    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState(user?.display_name || '');
    const [nameBusy, setNameBusy] = useState(false);
    // Prefer the live subscriptions row over cached user fields — the cache
    // can lag if a webhook didn't write the tier on activation.
    const subTier = (liveSub?.tier || user?.subscription_tier || '').toLowerCase();
    const subStatus = (liveSub?.status || user?.subscription_status || 'none').toLowerCase();
    const isPaid = ['trialing', 'active'].includes(subStatus) &&
        ['explorer', 'polyglot', 'global'].includes(subTier);
    const childCap = isPaid
        ? ({ explorer: 3, polyglot: 4, global: 5 }[subTier] || 1)
        : 1;
    const famGated = children.length >= childCap;

    useEffect(() => {
        api.get('/play/vocabulary').then(({ data }) => setVocab(data.vocabulary || []));
        api.get('/languages?status=live').then(({ data }) => setLanguages(data.languages.filter((l) => l.status === 'live')));
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get('/me/subscription');
                if (cancelled) return;
                const live = data?.subscription || null;
                setLiveSub(live);
                // Auto self-heal: if the live row says paid+tier but the cached
                // user field is stale, silently sync so subsequent reads see
                // correct values without the user clicking anything.
                const liveTier = (live?.tier || '').toLowerCase();
                const liveStatus = (live?.status || '').toLowerCase();
                const cachedTier = (user?.subscription_tier || '').toLowerCase();
                const cachedStatus = (user?.subscription_status || '').toLowerCase();
                const livePaid = ['active', 'trialing'].includes(liveStatus) &&
                    ['explorer', 'polyglot', 'global'].includes(liveTier);
                if (livePaid && (cachedTier !== liveTier || !['active', 'trialing'].includes(cachedStatus))) {
                    try {
                        await api.post('/me/subscription/sync');
                        await refreshUser?.();
                    } catch { /* non-fatal — UI uses live row anyway */ }
                }
            } catch { /* ignore */ }
        })();
        api.get('/me/family-progress')
            .then(({ data }) => setFam(data))
            .catch(() => {});
        api.get('/me/recent-activity?limit=8')
            .then(({ data }) => setRecent(data || { activity: [], streak_days: 0, days_played_total: 0 }))
            .catch(() => {});
        return () => { cancelled = true; };
    }, [children.length, user, refreshUser]);

    const activeChild = children.find((c) => c.id === activeChildId) || children[0];

    const saveName = async () => {
        const trimmed = (nameDraft || '').trim();
        if (trimmed.length < 2) return;
        setNameBusy(true);
        try {
            await api.patch('/me/display-name', { display_name: trimmed });
            await refreshUser?.();
            setEditingName(false);
        } finally { setNameBusy(false); }
    };

    return (
        <main className="max-w-7xl mx-auto px-6 py-14" data-testid="page-dashboard">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-sm uppercase tracking-wider text-lq-purple-500 font-semibold">Welcome back, {user?.display_name}</p>
                    <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">Your family dashboard</h1>
                </div>
                <div className="flex items-center gap-2 bg-white border border-lq-ink-100 rounded-full px-2 py-1">
                    <Users2 size={16} className="ml-2 text-lq-ink-500" />
                    <span className="text-sm text-lq-ink-500">Active:</span>
                    {children.length === 0 && !famGated && <Link to="/onboarding/add-child" className="pr-3 pl-1 text-lq-purple-700 font-semibold text-sm">Add a child →</Link>}
                    {children.length === 0 && famGated && <Link to="/pricing" className="pr-3 pl-1 text-lq-purple-700 font-semibold text-sm">Upgrade to add children →</Link>}
                    {children.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => pickActiveChild(c.id)}
                            className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors ${
                                (activeChild?.id === c.id) ? 'bg-lq-purple-500 text-white' : 'text-lq-ink-700 hover:bg-lq-purple-100'
                            }`}
                            data-testid={`switch-child-${c.display_name}`}
                        >{c.display_name}</button>
                    ))}
                    {children.length > 0 && !famGated && (
                        <Link to="/onboarding/add-child" className="p-1.5 rounded-full hover:bg-lq-purple-100 text-lq-purple-700" data-testid="dashboard-add-child">
                            <Plus size={16} />
                        </Link>
                    )}
                </div>
            </div>

            {children.length === 0 && !famGated && (
                <div className="mt-10 bg-lq-peach-100 border border-lq-peach-300 rounded-2xl p-6" data-testid="no-child-banner">
                    <h2 className="font-display text-xl font-semibold text-lq-ink-900">Add your first child profile</h2>
                    <p className="mt-1 text-sm text-lq-ink-700">You'll pick a name, age, and languages. A parental-consent record is captured at the same time, we need this to stay compliant.</p>
                    <Link to="/onboarding/add-child" className="mt-3 inline-flex bg-lq-purple-500 text-white font-semibold px-5 py-2 rounded-full">Add a child →</Link>
                </div>
            )}

            {/* Streak + Resume last puzzle + Recent activity */}
            {(recent.activity.length > 0 || recent.streak_days > 0) && (
                <section className="mt-10 grid md:grid-cols-3 gap-4" data-testid="dashboard-engagement">
                    <div className="bg-gradient-to-br from-lq-peach-100 to-lq-peach-300 rounded-2xl p-5 border border-lq-peach-300" data-testid="dashboard-streak">
                        <div className="text-xs uppercase tracking-wider text-lq-peach-600 font-semibold inline-flex items-center gap-1"><Flame size={12}/> Streak</div>
                        <div className="mt-1 font-display text-4xl text-lq-ink-black">{recent.streak_days}<span className="text-base text-lq-ink-700 font-sans"> day{recent.streak_days === 1 ? '' : 's'} in a row</span></div>
                        <p className="mt-1 text-xs text-lq-ink-700">{recent.streak_days === 0 ? 'Play any puzzle today to start a streak.' : 'Keep the family streak alive — one puzzle today does it.'}</p>
                    </div>
                    {recent.activity[0] && (
                        <div className="bg-white rounded-2xl border border-lq-ink-100 p-5 md:col-span-2 flex flex-col" data-testid="dashboard-resume">
                            <div className="text-xs uppercase tracking-wider text-lq-ink-500 font-semibold inline-flex items-center gap-1"><PlayIcon size={12}/> Pick up where you left off</div>
                            <div className="mt-1 font-display text-xl text-lq-ink-900">
                                {prettyPuzzle(recent.activity[0].puzzle_type)} — <span className="capitalize">{recent.activity[0].language_slug}</span>
                            </div>
                            <p className="mt-1 text-sm text-lq-ink-500">
                                Last played by <strong>{recent.activity[0].child?.display_name || 'you'}</strong> · {recent.activity[0].points_awarded} pts · {timeAgo(recent.activity[0].completed_at)}
                            </p>
                            <div className="mt-auto pt-4">
                                <Link
                                    to={`/languages/${recent.activity[0].language_slug}`}
                                    className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700"
                                    data-testid="dashboard-resume-cta"
                                >
                                    Play another <PlayIcon size={14}/>
                                </Link>
                            </div>
                        </div>
                    )}
                </section>
            )}

            {recent.activity.length > 0 && (
                <section className="mt-6 bg-white rounded-2xl border border-lq-ink-100 p-5" data-testid="dashboard-recent">
                    <div className="flex items-center justify-between">
                        <h2 className="font-display text-lg font-semibold text-lq-ink-900 inline-flex items-center gap-2"><History size={16}/> Recent activity</h2>
                        <span className="text-xs text-lq-ink-500">last {recent.activity.length} puzzles</span>
                    </div>
                    <ul className="mt-3 divide-y divide-lq-ink-100">
                        {recent.activity.map((a) => (
                            <li key={a.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`recent-${a.id}`}>
                                <div className="w-8 h-8 rounded-full bg-lq-purple-100 inline-flex items-center justify-center text-lq-purple-700 font-bold flex-shrink-0">{(a.child?.display_name || '·').charAt(0).toUpperCase()}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-lq-ink-900 truncate"><strong>{a.child?.display_name || 'You'}</strong> finished {prettyPuzzle(a.puzzle_type)} in <span className="capitalize">{a.language_slug}</span></div>
                                    <div className="text-xs text-lq-ink-500">{a.points_awarded} pts · {a.difficulty} · {timeAgo(a.completed_at)}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section className="mt-10 grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl border border-lq-ink-100 p-5">
                    <div className="text-xs text-lq-ink-500 uppercase tracking-wider font-semibold">Subscription</div>
                    <div className="mt-1 font-display text-xl text-lq-ink-900" data-testid="dashboard-sub-state">
                        {isPaid
                            ? `${subTier.charAt(0).toUpperCase() + subTier.slice(1)}${subStatus === 'trialing' ? ' (trial)' : ''}`
                            : 'Free tier'}
                    </div>
                    {isPaid && liveSub?.current_period_end && (
                        <p className="mt-1 text-xs text-lq-ink-500">Renews/expires {new Date(liveSub.current_period_end).toLocaleDateString()}{liveSub?.billing_period === 'gift' ? ' · gift card' : ''}</p>
                    )}
                    {!isPaid && (
                        <p className="mt-1 text-sm text-lq-ink-500">Word Search in Shona is free forever. Upgrade for every puzzle type and every language.</p>
                    )}
                    <Link to="/pricing" className="mt-3 inline-flex text-sm text-lq-purple-700 font-semibold hover:underline">{isPaid ? 'Manage plan' : 'See plans'} →</Link>
                    {!isPaid && (
                        <Link to="/redeem" className="mt-2 block text-xs text-lq-ink-500 hover:text-lq-purple-700" data-testid="dashboard-redeem-link">Have a gift card? Redeem it →</Link>
                    )}
                </div>
                <div className="bg-white rounded-2xl border border-lq-ink-100 p-5">
                    <div className="text-xs text-lq-ink-500 uppercase tracking-wider font-semibold inline-flex items-center gap-1"><Gift size={12} /> Referral</div>
                    <div className="mt-1 font-display text-xl text-lq-ink-900">{user?.referral_code}</div>
                    <p className="mt-1 text-sm text-lq-ink-500">A puzzle a day keeps the monolingual away. Give a month, get a month.</p>
                    <button
                        onClick={() => navigator.clipboard.writeText(`${window.location.origin}/register?ref=${user?.referral_code}`)}
                        className="mt-3 text-sm text-lq-purple-700 font-semibold hover:underline"
                        data-testid="copy-referral"
                    >Copy invite link</button>
                </div>
                <div className="bg-white rounded-2xl border border-lq-ink-100 p-5">
                    <div className="text-xs text-lq-ink-500 uppercase tracking-wider font-semibold inline-flex items-center gap-1"><BookMarked size={12} /> Vocabulary</div>
                    <div className="mt-1 font-display text-xl text-lq-ink-900">{vocab.length} words learned</div>
                    <p className="mt-1 text-sm text-lq-ink-500">Saved by your children as they play.</p>
                </div>
            </section>

            {activeChild && (
                <section className="mt-6">
                    <DifficultyPicker child={activeChild} onChanged={() => refreshChildren?.()} />
                </section>
            )}

            {/* FAMILY POINTS + USERNAME EDITOR */}
            {fam && !isPaid && (
                <p className="mt-6 text-xs text-lq-ink-500" data-testid="parent-administer-note">
                    You&rsquo;re on the free plan &mdash; only your child earns points. <Link to="/pricing" className="text-lq-purple-700 hover:underline">Upgrade</Link> to play and earn points alongside them.
                </p>
            )}

            {/* FAMILY POINTS + USERNAME EDITOR */}
            {fam && (
                <section className="mt-6 grid md:grid-cols-[2fr,1fr] gap-4" data-testid="family-progress-section">
                    <div className="bg-white rounded-2xl border border-lq-ink-100 p-5">
                        <div className="flex items-center justify-between">
                            <div className="text-xs text-lq-ink-500 uppercase tracking-wider font-semibold inline-flex items-center gap-1"><Trophy size={12}/> Family points</div>
                            <Link to="/leaderboard" className="text-xs text-lq-purple-700 hover:underline">See leaderboard →</Link>
                        </div>
                        <div className="mt-1 font-display text-3xl text-lq-ink-900" data-testid="family-total-points">{fam.family_total_points?.toLocaleString?.() || 0}<span className="text-base text-lq-ink-500 font-sans font-normal"> pts total</span></div>
                        <ul className="mt-4 space-y-2">
                            <li className="flex items-center gap-3 text-sm" data-testid="family-row-parent">
                                <div className="w-8 h-8 rounded-full bg-lq-purple-100 inline-flex items-center justify-center text-lq-purple-700 font-bold">{(fam.parent?.display_name || '?').charAt(0).toUpperCase()}</div>
                                <div className="flex-1 min-w-0 font-display font-semibold text-lq-ink-900 truncate">{fam.parent?.display_name} <span className="text-xs text-lq-ink-500 font-sans font-normal">(you)</span></div>
                                <div className="font-display font-semibold text-lq-ink-900">{fam.parent?.points || 0}</div>
                            </li>
                            {fam.children.map((c) => (
                                <li key={c.id} className="flex items-center gap-3 text-sm" data-testid={`family-row-${c.display_name}`}>
                                    <div className="w-8 h-8 rounded-full bg-lq-peach-100 inline-flex items-center justify-center text-lq-peach-600 font-bold">{c.display_name.charAt(0).toUpperCase()}</div>
                                    <div className="flex-1 min-w-0 font-display font-semibold text-lq-ink-900 truncate">{c.display_name} <span className="text-xs text-lq-ink-500 font-sans font-normal">{c.age_tier_slug}</span></div>
                                    <div className="font-display font-semibold text-lq-ink-900">{c.points || 0}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="bg-white rounded-2xl border border-lq-ink-100 p-5">
                        <div className="text-xs text-lq-ink-500 uppercase tracking-wider font-semibold inline-flex items-center gap-1"><UserCog size={12}/> Your display name</div>
                        <p className="mt-1 text-sm text-lq-ink-500">This is the only thing shown on public leaderboards. Change it any time.</p>
                        {editingName ? (
                            <div className="mt-3 flex gap-2">
                                <input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} maxLength={30} minLength={2}
                                    className="flex-1 px-3 py-2 rounded-lg border border-lq-ink-300 bg-white focus:border-lq-purple-500 outline-none"
                                    data-testid="dashboard-displayname-input"/>
                                <button onClick={saveName} disabled={nameBusy} className="px-3 py-2 rounded-lg bg-lq-purple-500 text-white font-semibold disabled:opacity-60" data-testid="dashboard-displayname-save">
                                    <Save size={14}/>
                                </button>
                            </div>
                        ) : (
                            <div className="mt-3 flex items-center justify-between">
                                <div className="font-display text-xl text-lq-ink-900" data-testid="dashboard-displayname-value">{user?.display_name}</div>
                                <button onClick={() => { setNameDraft(user?.display_name || ''); setEditingName(true); }} className="text-sm text-lq-purple-700 font-semibold hover:underline" data-testid="dashboard-displayname-edit">Change</button>
                            </div>
                        )}
                    </div>
                </section>
            )}

            <section className="mt-10">
                <h2 className="font-display text-2xl font-semibold text-lq-ink-900">Pick a language to play</h2>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dashboard-languages">
                    {languages.map((l) => (
                        <Link
                            key={l.id}
                            to={`/languages/${l.slug}`}
                            className="group bg-white border border-lq-ink-100 rounded-2xl p-4 hover:border-lq-purple-300 hover:shadow transition-all text-center"
                        >
                            <div className="text-4xl">{l.primary_flag_emoji}</div>
                            <div className="mt-2 font-display font-semibold text-lq-ink-900">{l.name}</div>
                        </Link>
                    ))}
                </div>
            </section>

            {vocab.length > 0 && (
                <section className="mt-10">
                    <h2 className="font-display text-2xl font-semibold text-lq-ink-900">Saved vocabulary</h2>
                    <div className="mt-4 bg-white rounded-2xl border border-lq-ink-100 divide-y divide-lq-ink-100">
                        {vocab.slice(0, 20).map((v) => (
                            <div key={v.id} className="flex items-center justify-between px-5 py-3">
                                <div>
                                    <div className="font-display text-lg text-lq-ink-900">{v.foreign_word} <span className="text-lq-ink-500 text-base">→ {v.english_translation}</span></div>
                                    <div className="text-xs text-lq-ink-500">{v.language_name} · saved {new Date(v.saved_at).toLocaleDateString()}</div>
                                </div>
                                <SpeakerButton wordId={v.word_id} text={v.foreign_word} languageSlug={v.language_slug} />
                            </div>
                        ))}
                    </div>
                </section>
            )}
            <UpgradeNudge />
            <PartnerAdRotator />
        </main>
    );
}

const PUZZLE_LABELS = {
    word_search: 'Word Search',
    balloon_pop: 'Balloon Pop',
    word_scramble: 'Word Scramble',
    crossword: 'Crossword',
    missing_vowel: 'Missing Vowel',
    cryptogram: 'Cryptogram',
};
function prettyPuzzle(slug) {
    return PUZZLE_LABELS[slug] || (slug || '').replace(/_/g, ' ');
}
function timeAgo(iso) {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
}

